import { Fragment, type ReactNode } from 'react';
import type { Idioma } from '../../../i18n/index.js';
import type { RespuestaDeAthena } from '../../../common/services/tipos.js';

/**
 * Convierte las marcas `[n]` del texto en enlaces a su fuente, y la negrita y la cursiva de
 * markdown en su formato.
 *
 * Las marcas son la pieza que hace visible la promesa del producto: cada cifra lleva al estudio del
 * que sale. Una marca que no tenga fuente **se deja como texto plano** y no se convierte en un
 * enlace roto: un enlace que no lleva a ninguna parte miente más que un número suelto.
 *
 * **Markdown básico, y solo eso** (Iván, 2026-09-25): el compositor escribe subtítulos como
 * `**Evolución.**` y la web enseñaba los asteriscos. Se interpretan `**negrita**` y `*cursiva*`, sin
 * HTML: React escapa el texto, así que nada de lo que escriba el modelo se convierte en marcado.
 */
const MARKDOWN = /\*\*(?!\s)(.+?)(?<!\s)\*\*|(?<![\w*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\w*])/gu;

export function partesConEstilo(texto: string): { texto: string; estilo: 'normal' | 'negrita' | 'cursiva' }[] {
  const partes: { texto: string; estilo: 'normal' | 'negrita' | 'cursiva' }[] = [];
  let desde = 0;
  for (const m of texto.matchAll(MARKDOWN)) {
    if (m.index > desde) partes.push({ texto: texto.slice(desde, m.index), estilo: 'normal' });
    partes.push(m[1] === undefined ? { texto: m[2]!, estilo: 'cursiva' } : { texto: m[1], estilo: 'negrita' });
    desde = m.index + m[0].length;
  }
  if (desde < texto.length) partes.push({ texto: texto.slice(desde), estilo: 'normal' });
  return partes;
}

export function TextoConMarcas({
  texto,
  respuesta,
  idioma: _idioma,
}: {
  readonly texto: string;
  readonly respuesta: RespuestaDeAthena;
  readonly idioma: Idioma;
}) {
  return (
    <>
      {partesConEstilo(texto).map((parte, i) => {
        const contenido = conMarcas(parte.texto, respuesta, `${String(i)}`);
        if (parte.estilo === 'negrita') return <strong key={`${String(i)}-b`}>{contenido}</strong>;
        if (parte.estilo === 'cursiva') return <em key={`${String(i)}-i`}>{contenido}</em>;
        return <Fragment key={`${String(i)}-n`}>{contenido}</Fragment>;
      })}
    </>
  );
}

/** `[17, 64]` es `[17][64]`: respuestas guardadas antes de que el API lo separe (2026-09-27). */
const VARIAS_EN_UN_CORCHETE = /\[(\d+(?:\s*[,;y]\s*\d+)+)\]/gu;

function conMarcas(texto: string, respuesta: RespuestaDeAthena, clave: string): ReactNode[] {
  return texto
    .replace(VARIAS_EN_UN_CORCHETE, (_, dentro: string) =>
      dentro
        .split(/\s*[,;y]\s*/u)
        .map(n => `[${n}]`)
        .join(''),
    )
    .split(/(\[\d+\])/gu)
    .map((trozo, i) => {
      const k = `${clave}-${String(i)}`;
      const marca = /^\[(\d+)\]$/u.exec(trozo);
      if (marca === null) return <Fragment key={`${k}-t`}>{trozo}</Fragment>;

      const n = Number(marca[1]);
      const referencia = respuesta.referencias.find(r => r.n === n);
      const fuente = referencia === undefined ? undefined : respuesta.fuentes.find(f => f.hechoId === referencia.hechoId);
      if (fuente === undefined) return <Fragment key={`${k}-h`}>{trozo}</Fragment>;

      return (
        <a
          key={`${k}-m`}
          href={`#f${String(n)}-${respuesta.preguntaId}`}
          className='ml-[1px] align-super font-(family-name:--mono) text-[11px] text-(--teal-deep) no-underline'
          aria-label={`${fuente.editor}${fuente.pagina === null ? '' : `, p. ${String(fuente.pagina)}`}`}
        >
          [{String(n)}]
        </a>
      );
    });
}
